/* bb-bundle.js
   Widget cross-sell "Souvent acheté ensemble".
   Lit les données Liquid déjà présentes dans le DOM : aucun fetch produit.
*/

(function () {
  if (window.customElements && customElements.get('bb-bundle')) return;

  class BbBundle extends HTMLElement {
    constructor() {
      super();
      this.data = null;
      this.form = null;
      this.productSection = null;
      this.themeSection = null;
      this.addButton = null;
      this.idInput = null;
      this.quantityInput = null;
      this.defaultButtonText = '';
      this.boundFormSubmit = this.handleFormSubmit.bind(this);
      this.boundAddButtonClick = this.handleAddButtonClick.bind(this);
      this.boundGlobalAddIntent = this.handleGlobalAddIntent.bind(this);
      this.boundGlobalSubmitIntent = this.handleGlobalSubmitIntent.bind(this);
      this.boundCartAddFallback = this.handleCartAddFallback.bind(this);
      this.pendingFallbackTimer = null;
      this.isBundleSubmitting = false;
      this.installCartAddRequestFallback();
    }

    connectedCallback() {
      this.data = this.readData();
      if (!this.data || !Array.isArray(this.data.products) || this.data.products.length === 0) {
        this.hidden = true;
        return;
      }

      this.form = this.closest('form');
      this.productSection = this.closest('.cc-main-product');
      this.themeSection = this.closest('.shopify-section');
      this.addButton = this.form ? this.form.querySelector('[name="add"]') : null;
      this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.quantityInput = this.form ? this.form.querySelector('[name="quantity"]') : null;
      this.defaultButtonText = this.addButton
        ? this.addButton.dataset.addToCartText || this.addButton.textContent.trim()
        : this.dataset.defaultButtonLabel || 'Add to cart';

      this.bindEvents();
      this.syncAllRows();
      this.updateSummary();
    }

    disconnectedCallback() {
      if (BbBundle.fallbackInstances) {
        BbBundle.fallbackInstances.delete(this);
      }

      if (this.form) {
        this.form.removeEventListener('submit', this.boundFormSubmit, { capture: true });
      }

      if (this.addButton) {
        this.addButton.removeEventListener('click', this.boundAddButtonClick, { capture: true });
      }

      window.removeEventListener('click', this.boundGlobalAddIntent, { capture: true });
      window.removeEventListener('pointerup', this.boundGlobalAddIntent, { capture: true });
      window.removeEventListener('touchend', this.boundGlobalAddIntent, { capture: true });
      window.removeEventListener('submit', this.boundGlobalSubmitIntent, { capture: true });

      if (this.pendingFallbackTimer) {
        window.clearTimeout(this.pendingFallbackTimer);
        this.pendingFallbackTimer = null;
      }
    }

    /**
     * Retourne les lignes complémentaires cochées pour ProductForm.
     * @returns {Array<{id: number, quantity: number}>}
     */
    getSelectedItems() {
      return this.rows.reduce((items, row) => {
        const checkbox = row.querySelector('[data-bb-bundle-checkbox]');
        const variant = this.getRowVariant(row);

        if (checkbox && checkbox.checked && !checkbox.disabled && variant && variant.available) {
          items.push({ id: variant.id, quantity: 1 });
        }

        return items;
      }, []);
    }

    /**
     * Lit le JSON injecté par Liquid.
     * @returns {?object}
     */
    readData() {
      const dataEl = this.querySelector('[data-bb-bundle-data]');
      if (!dataEl) return null;

      try {
        return JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('[bb-bundle] JSON invalide', error);
        return null;
      }
    }

    bindEvents() {
      this.addEventListener('change', (evt) => {
        if (evt.target.matches('[data-bb-bundle-select]')) {
          this.syncRow(evt.target.closest('[data-bb-bundle-item]'));
        }

        if (evt.target.matches('[data-bb-bundle-checkbox], [data-bb-bundle-select]')) {
          this.updateSummary();
        }
      });

      if (this.idInput) {
        this.idInput.addEventListener('change', () => this.updateSummary());
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => this.updateSummary());
        this.quantityInput.addEventListener('input', () => this.updateSummary());
      }

      if (this.form) {
        this.form.addEventListener('submit', this.boundFormSubmit, { capture: true });
      }

      if (this.addButton) {
        this.addButton.addEventListener('click', this.boundAddButtonClick, { capture: true });
      }

      window.addEventListener('click', this.boundGlobalAddIntent, { capture: true });
      window.addEventListener('pointerup', this.boundGlobalAddIntent, { capture: true });
      window.addEventListener('touchend', this.boundGlobalAddIntent, { capture: true });
      window.addEventListener('submit', this.boundGlobalSubmitIntent, { capture: true });

      document.addEventListener('on:variant:change', (evt) => {
        if (!evt.detail || evt.detail.form !== this.form) return;
        this.updateSummary();
      });
    }

    handleGlobalAddIntent(evt) {
      const target = this.getEventTarget(evt);
      const submitter = target
        ? target.closest('button[name="add"], button[type="submit"], input[type="submit"]')
        : null;

      this.interceptBundleSubmit(evt, submitter);
    }

    handleCartAddFallback() {
      if (this.isBundleSubmitting) return;

      this.queueSelectedItemsFallback();
    }

    handleGlobalSubmitIntent(evt) {
      const target = this.getEventTarget(evt);
      const form = target && target.matches('form') ? target : null;
      const submitter = evt.submitter
        || this.getActiveSubmitter(form)
        || (form === this.form ? this.addButton : form?.querySelector('button[type="submit"], input[type="submit"]'));

      this.interceptBundleSubmit(evt, submitter, form);
    }

    handleAddButtonClick(evt) {
      this.interceptBundleSubmit(evt, this.addButton);
    }

    handleFormSubmit(evt) {
      this.interceptBundleSubmit(evt, evt.submitter || this.getActiveSubmitter(this.form) || this.addButton, this.form);
    }

    queueSelectedItemsFallback() {
      const selectedItems = this.getSelectedItems();
      if (selectedItems.length === 0) return;

      const submittedAt = Date.now();
      const oldCartPromise = this.fetchCart();

      if (this.pendingFallbackTimer) {
        window.clearTimeout(this.pendingFallbackTimer);
      }

      this.pendingFallbackTimer = window.setTimeout(() => {
        this.pendingFallbackTimer = null;
        this.addSelectedItemsFallback(selectedItems, submittedAt, oldCartPromise);
      }, 700);
    }

    getEventTarget(evt) {
      if (!evt || !evt.target) return null;
      return evt.target instanceof Element ? evt.target : evt.target.parentElement;
    }

    getActiveSubmitter(form) {
      const activeElement = document.activeElement;
      if (!form || !(activeElement instanceof Element) || !form.contains(activeElement)) return null;

      return activeElement.closest(
        'button[name="add"], button[type="submit"], input[type="submit"], .shopify-payment-button__button'
      );
    }

    interceptBundleSubmit(evt, submitter, form) {
      const context = this.getSubmitContext(submitter, form);
      if (!context) return false;

      const selectedItems = this.getSelectedItems();
      if (selectedItems.length === 0) return false;

      if (this.isBundleSubmitting) {
        if (evt) {
          evt.preventDefault();
          if (typeof evt.stopImmediatePropagation === 'function') {
            evt.stopImmediatePropagation();
          }
        }
        return true;
      }

      if (this.isSubmitterUnavailable(context)) return false;

      if (
        context.form
        && typeof context.form.checkValidity === 'function'
        && !context.form.checkValidity()
      ) {
        return false;
      }

      const mainLineItem = this.buildLineItemFromForm(context.form || this.form);
      if (!mainLineItem) return false;

      if (evt) {
        evt.preventDefault();
        if (typeof evt.stopImmediatePropagation === 'function') {
          evt.stopImmediatePropagation();
        }
      }

      this.submitBundle(context, selectedItems, mainLineItem);
      return true;
    }

    isRecentlyThemeHandled(windowMs = 1200) {
      const themeHandledAt = Number(this.form?.dataset.bbBundleThemeHandledAt || 0);
      return themeHandledAt > 0 && Date.now() - themeHandledAt < windowMs;
    }

    getSubmitContext(submitter, form) {
      if (!this.form) return null;

      if (submitter && this.isDynamicCheckout(submitter)) return null;

      if (form === this.form) {
        return {
          form: this.form,
          button: submitter || this.addButton,
          stickyPanel: null
        };
      }

      if (submitter && this.form.contains(submitter)) {
        return {
          form: this.form,
          button: submitter,
          stickyPanel: null
        };
      }

      const stickyPanel = submitter?.closest('sticky-atc-panel') || form?.closest('sticky-atc-panel');
      if (
        stickyPanel
        && (
          (this.productSection && this.productSection.contains(stickyPanel))
          || (this.themeSection && this.themeSection.contains(stickyPanel))
        )
      ) {
        return {
          form: stickyPanel.querySelector('[data-sticky-atc-form]') || form || submitter?.closest('form'),
          button: submitter || stickyPanel.querySelector('button[type="submit"], input[type="submit"]'),
          stickyPanel
        };
      }

      return null;
    }

    matchesMainVariant(variantId) {
      const id = parseInt(variantId, 10);
      if (!id) return false;

      const mainId = parseInt(this.form?.querySelector('[name="id"]')?.value, 10);
      const stickyId = parseInt(this.themeSection?.querySelector('[data-sticky-atc-id]')?.value, 10);

      return id === mainId || id === stickyId;
    }

    isDynamicCheckout(submitter) {
      return Boolean(
        submitter.matches('.shopify-payment-button__button')
          || submitter.closest('.shopify-payment-button')
      );
    }

    isSubmitterUnavailable(context) {
      const { button, stickyPanel } = context;
      if (!button) return true;

      return Boolean(
        button.disabled
          || button.getAttribute('aria-disabled') === 'true'
          || button.classList.contains('is-loading')
          || button.classList.contains('is-success')
          || button.dataset.needsPicker === 'true'
          || stickyPanel?.dataset.needsPicker === 'true'
      );
    }

    async submitBundle(context, selectedItems, mainLineItem) {
      const submittedAt = Date.now();
      this.isBundleSubmitting = true;
      if (this.form) this.form.dataset.bbBundleThemeHandledAt = String(submittedAt);

      if (this.pendingFallbackTimer) {
        window.clearTimeout(this.pendingFallbackTimer);
        this.pendingFallbackTimer = null;
      }

      this.setSubmitterLoading(context, true);

      try {
        const response = await fetch(this.cartAddRoute, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [
              mainLineItem,
              ...selectedItems
            ],
            sections: this.sectionsToRender,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || response.status);

        this.updateCartIcon(data);
        await this.notifyCartUpdated(data);
        this.setSubmitterSuccess(context);
      } catch (error) {
        console.error('[bb-bundle] Ajout groupé impossible', error);
        this.setSubmitterError(context);
      } finally {
        window.setTimeout(() => {
          this.resetSubmitterState(context);
        }, 1400);
        this.isBundleSubmitting = false;
      }
    }

    buildLineItemFromForm(form) {
      if (!form) return null;

      const formData = new FormData(form);
      const id = parseInt(formData.get('id'), 10);
      if (!id) return null;

      const quantity = Math.max(parseInt(formData.get('quantity'), 10) || 1, 1);
      const lineItem = { id, quantity };
      const sellingPlan = formData.get('selling_plan');
      const properties = {};

      if (sellingPlan) lineItem.selling_plan = sellingPlan;

      formData.forEach((value, key) => {
        if (!key.startsWith('properties[') || value === '') return;

        const propertyName = key.slice(11, -1);
        properties[propertyName] = value;
      });

      if (Object.keys(properties).length > 0) {
        lineItem.properties = properties;
      }

      return lineItem;
    }

    setSubmitterLoading(context, isLoading) {
      const { button } = context;
      if (!button) return;

      button.classList.toggle('is-loading', isLoading);
      if (isLoading) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }

    setSubmitterSuccess(context) {
      const { button } = context;
      if (!button) return;
      button.classList.remove('is-loading', 'is-error');
      button.classList.add('is-success');
    }

    setSubmitterError(context) {
      const { button } = context;
      if (!button) return;
      button.classList.remove('is-loading', 'is-success');
      button.classList.add('is-error');
    }

    resetSubmitterState(context) {
      const { button } = context;
      if (!button) return;
      button.classList.remove('is-loading', 'is-success', 'is-error');
      button.removeAttribute('aria-disabled');
    }

    get rows() {
      return Array.from(this.querySelectorAll('[data-bb-bundle-item]'));
    }

    syncAllRows() {
      this.rows.forEach((row) => this.syncRow(row));
    }

    /**
     * Met à jour prix, disponibilité et id de variante d'une ligne.
     * @param {Element} row - Ligne produit.
     */
    syncRow(row) {
      if (!row) return;

      const variant = this.getRowVariant(row);
      const checkbox = row.querySelector('[data-bb-bundle-checkbox]');
      const priceEl = row.querySelector('[data-bb-bundle-price]');
      const compareEl = row.querySelector('[data-bb-bundle-compare]');
      const statusEl = row.querySelector('[data-bb-bundle-status]');

      if (!variant) {
        row.classList.add('is-unavailable');
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = true;
        }
        if (statusEl) statusEl.textContent = this.dataset.soldOutLabel || 'Sold out';
        return;
      }

      if (checkbox) {
        checkbox.dataset.variantId = variant.id;
        checkbox.disabled = !variant.available;
        if (!variant.available) checkbox.checked = false;
      }

      row.classList.toggle('is-unavailable', !variant.available);
      if (statusEl) {
        statusEl.textContent = variant.available
          ? (this.dataset.inStockLabel || 'In stock')
          : (this.dataset.soldOutLabel || 'Sold out');
      }
      if (priceEl) priceEl.textContent = this.formatMoney(variant.price);

      if (compareEl) {
        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareEl.hidden = !hasCompare;
        compareEl.textContent = hasCompare ? this.formatMoney(variant.compare_at_price) : '';
      }
    }

    /**
     * Récupère la variante choisie dans une ligne.
     * @param {Element} row - Ligne produit.
     * @returns {?object}
     */
    getRowVariant(row) {
      const productId = Number(row.dataset.productId);
      const product = this.data.products.find((entry) => Number(entry.id) === productId);
      if (!product || !Array.isArray(product.variants)) return null;

      const select = row.querySelector('[data-bb-bundle-select]');
      const selectedId = select
        ? Number(select.value)
        : Number(row.querySelector('[data-bb-bundle-checkbox]')?.dataset.variantId || product.selectedVariantId);

      return product.variants.find((variant) => Number(variant.id) === selectedId) || null;
    }

    getMainQuantity() {
      const quantity = Number(this.quantityInput ? this.quantityInput.value : 1);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    async addSelectedItemsFallback(selectedItems, submittedAt, oldCartPromise) {
      const themeHandledAt = Number(this.form?.dataset.bbBundleThemeHandledAt || 0);
      if (themeHandledAt >= submittedAt) return;

      const oldCart = await oldCartPromise.catch(() => null);
      const currentCart = oldCart ? await this.fetchCart().catch(() => null) : null;
      const missingItems = this.getMissingItems(selectedItems, oldCart, currentCart);
      if (missingItems.length === 0) return;

      try {
        let data = null;

        for (const item of missingItems) {
          const response = await fetch(this.cartAddRoute, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              id: item.id,
              quantity: item.quantity,
              sections: 'cart-icon-bubble',
              sections_url: window.location.pathname
            })
          });
          data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || response.status);
        }

        this.updateCartIcon(data);
        await this.notifyCartUpdated(data);
      } catch (error) {
        console.error('[bb-bundle] Ajout des produits associés impossible', error);
      }
    }

    installCartAddRequestFallback() {
      BbBundle.fallbackInstances = BbBundle.fallbackInstances || new Set();
      BbBundle.fallbackInstances.add(this);

      if (BbBundle.cartAddFallbackInstalled) return;
      BbBundle.cartAddFallbackInstalled = true;

      const notifyFallbackInstances = () => {
        (BbBundle.fallbackInstances || []).forEach((instance) => {
          if (instance && document.contains(instance)) {
            instance.boundCartAddFallback();
          }
        });
      };

      const createFetchHandler = (originalFetch) => {
        return async function (fetchThis, argsLike) {
          const args = Array.from(argsLike);
          const transformedArgs = await BbBundle.withBundleItems(args[0], args[1]);
          const requestArgs = transformedArgs || args;
          const shouldFallback = !transformedArgs && BbBundle.isSingleCartAddRequest(args[0], args[1]);

          return originalFetch.apply(fetchThis, requestArgs).then((response) => {
            if (transformedArgs && response.ok) {
              response.clone().json()
                .then((data) => BbBundle.notifyTransformedCartAdd(data))
                .catch(() => BbBundle.notifyTransformedCartAdd(null));
            }

            if (shouldFallback && response.ok) {
              window.setTimeout(notifyFallbackInstances, 120);
            }

            return response;
          });
        };
      };

      if (typeof window.fetch === 'function' && !window.fetch.__bbBundleCartAddFallback) {
        const originalFetch = window.fetch.__bbBundleOriginalFetch || window.fetch;
        const fetchHandler = createFetchHandler(originalFetch);

        if (window.__bbBundleFetchHook && window.fetch.__bbBundleOriginalFetch) {
          window.__bbBundleFetchHandler = fetchHandler;
          window.fetch.__bbBundleCartAddFallback = true;
        } else {
          const wrappedFetch = function (...args) {
            return fetchHandler(this, args);
          };

          wrappedFetch.__bbBundleCartAddFallback = true;
          wrappedFetch.__bbBundleOriginalFetch = originalFetch;
          window.fetch = wrappedFetch;
        }
      }

      if (window.XMLHttpRequest && !XMLHttpRequest.prototype.open.__bbBundleCartAddFallback) {
        const originalOpen = XMLHttpRequest.prototype.open;
        const originalSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function (method, url, ...args) {
          this.__bbBundleCartAddFallbackUrl = url;
          this.__bbBundleCartAddFallbackMethod = method;
          return originalOpen.call(this, method, url, ...args);
        };

        XMLHttpRequest.prototype.open.__bbBundleCartAddFallback = true;

        XMLHttpRequest.prototype.send = function (body) {
          const transformedBody = BbBundle.getBundleAddBody(
            this.__bbBundleCartAddFallbackUrl,
            this.__bbBundleCartAddFallbackMethod,
            body
          );
          const sendBody = transformedBody || body;
          const shouldFallback = !transformedBody && BbBundle.isSingleCartAddUrl(
            this.__bbBundleCartAddFallbackUrl,
            this.__bbBundleCartAddFallbackMethod,
            body
          );

          if (transformedBody) {
            this.setRequestHeader('Content-Type', 'application/json');
            this.setRequestHeader('Accept', 'application/json');
            this.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
            this.addEventListener('loadend', () => {
              if (this.status < 200 || this.status >= 300) return;

              let data = null;
              try {
                data = this.responseText ? JSON.parse(this.responseText) : null;
              } catch (error) {
                data = null;
              }

              BbBundle.notifyTransformedCartAdd(data);
            }, { once: true });
          }

          if (shouldFallback) {
            this.addEventListener('loadend', () => {
              if (this.status >= 200 && this.status < 300) {
                window.setTimeout(notifyFallbackInstances, 120);
              }
            }, { once: true });
          }

          return originalSend.call(this, sendBody);
        };
      }
    }

    static async withBundleItems(input, init = {}) {
      init = init || {};
      const method = init.method || (input instanceof Request ? input.method : 'POST');
      const url = typeof input === 'string' ? input : input?.url;
      const hasInitBody = Object.prototype.hasOwnProperty.call(init, 'body');
      const body = hasInitBody
        ? init.body
        : await BbBundle.readRequestBody(input);
      const transformedBody = BbBundle.getBundleAddBody(url, method, body);
      if (!transformedBody) return null;

      const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
      headers.set('Accept', 'application/json');
      headers.set('Content-Type', 'application/json');
      headers.set('X-Requested-With', 'XMLHttpRequest');

      if (input instanceof Request) {
        return [new Request(input, {
          ...init,
          method,
          headers,
          body: transformedBody
        })];
      }

      return [
        input,
        {
          ...init,
          method,
          headers,
          body: transformedBody
        }
      ];
    }

    static notifyTransformedCartAdd(data) {
      const bundle = Array.from(BbBundle.fallbackInstances || []).find((instance) => {
        return instance && document.contains(instance);
      });

      if (!bundle) return;
      if (data) bundle.updateCartIcon(data);

      bundle.notifyCartUpdated(data || {})
        .catch((error) => {
          console.error('[bb-bundle] Rafraîchissement panier impossible', error);
        });
    }

    static async readRequestBody(input) {
      if (!(input instanceof Request) || input.bodyUsed) return undefined;

      const contentType = input.headers.get('content-type') || '';

      try {
        if (contentType.includes('application/json') || contentType.includes('application/x-www-form-urlencoded')) {
          return await input.clone().text();
        }

        if (typeof input.clone().formData === 'function') {
          try {
            return await input.clone().formData();
          } catch (formError) {
            return await input.clone().text();
          }
        }

        return await input.clone().text();
      } catch (error) {
        return undefined;
      }
    }

    static getBundleAddBody(url, method = 'POST', body) {
      if (!BbBundle.isSingleCartAddUrl(url, method, body)) return null;

      const lineItem = BbBundle.buildLineItemFromBody(body);
      if (!lineItem) return null;

      const bundle = BbBundle.getBundleForMainVariant(lineItem.id);
      if (!bundle || bundle.isBundleSubmitting) return null;

      const selectedItems = bundle.getSelectedItems();
      if (selectedItems.length === 0) return null;

      if (bundle.form) {
        bundle.form.dataset.bbBundleThemeHandledAt = String(Date.now());
      }

      const extras = BbBundle.getBodyExtras(body);
      return JSON.stringify({
        items: [
          lineItem,
          ...selectedItems
        ],
        ...extras
      });
    }

    static getBundleForMainVariant(variantId) {
      const instances = Array.from(BbBundle.fallbackInstances || []);
      return instances.find((instance) => {
        return instance
          && document.contains(instance)
          && instance.matchesMainVariant(variantId)
          && instance.getSelectedItems().length > 0;
      }) || null;
    }

    static buildLineItemFromBody(body) {
      const data = BbBundle.parseBodyData(body);
      const lineItems = BbBundle.getLineItemsFromData(data);
      return lineItems.length === 1 ? lineItems[0] : null;
    }

    static getLineItemsFromData(data) {
      if (!data) return [];

      if (Array.isArray(data.items)) {
        return data.items.map((item) => BbBundle.normalizeLineItemData(item)).filter(Boolean);
      }

      const keyedItems = {};
      Object.keys(data).forEach((key) => {
        const itemMatch = key.match(/^items\[(\d+)]\[(id|quantity|selling_plan)]$/);
        const propertyMatch = key.match(/^items\[(\d+)]\[properties]\[(.+)]$/);

        if (itemMatch) {
          keyedItems[itemMatch[1]] = keyedItems[itemMatch[1]] || {};
          keyedItems[itemMatch[1]][itemMatch[2]] = data[key];
        }

        if (propertyMatch) {
          keyedItems[propertyMatch[1]] = keyedItems[propertyMatch[1]] || {};
          keyedItems[propertyMatch[1]].properties = keyedItems[propertyMatch[1]].properties || {};
          keyedItems[propertyMatch[1]].properties[propertyMatch[2]] = data[key];
        }
      });

      const keyedLineItems = Object.keys(keyedItems)
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => BbBundle.normalizeLineItemData(keyedItems[key]))
        .filter(Boolean);

      if (keyedLineItems.length > 0) return keyedLineItems;

      const singleLineItem = BbBundle.normalizeLineItemData(data);
      return singleLineItem ? [singleLineItem] : [];
    }

    static normalizeLineItemData(data) {
      if (!data || typeof data !== 'object') return null;

      const id = parseInt(data.id, 10);
      if (!id) return null;

      const quantity = Math.max(parseInt(data.quantity, 10) || 1, 1);
      const lineItem = { id, quantity };
      const sellingPlan = data.selling_plan;
      const properties = {};

      if (sellingPlan) lineItem.selling_plan = sellingPlan;

      if (data.properties && typeof data.properties === 'object') {
        Object.keys(data.properties).forEach((key) => {
          if (data.properties[key] === '') return;
          properties[key] = data.properties[key];
        });
      }

      Object.keys(data).forEach((key) => {
        const match = key.match(/^properties\[(.+)]$/);
        if (!match || data[key] === '') return;
        properties[match[1]] = data[key];
      });

      if (Object.keys(properties).length > 0) {
        lineItem.properties = properties;
      }

      return lineItem;
    }

    static getBodyExtras(body) {
      const data = BbBundle.parseBodyData(body) || {};
      const extras = {};

      if (data.sections) extras.sections = data.sections;
      if (data.sections_url) extras.sections_url = data.sections_url;

      return extras;
    }

    static parseBodyData(body) {
      if (!body) return null;

      if (body instanceof FormData || body instanceof URLSearchParams) {
        return Array.from(body.entries()).reduce((data, entry) => {
          data[entry[0]] = entry[1];
          return data;
        }, {});
      }

      if (typeof body === 'string') {
        try {
          return JSON.parse(body);
        } catch (error) {
          try {
            return Array.from(new URLSearchParams(body).entries()).reduce((data, entry) => {
              data[entry[0]] = entry[1];
              return data;
            }, {});
          } catch (paramsError) {
            return null;
          }
        }
      }

      return null;
    }

    static isSingleCartAddRequest(input, init = {}) {
      init = init || {};
      const method = init.method || (input instanceof Request ? input.method : 'POST');
      const url = typeof input === 'string' ? input : input?.url;
      const body = init.body || (input instanceof Request ? null : undefined);

      return BbBundle.isSingleCartAddUrl(url, method, body);
    }

    static isSingleCartAddUrl(url, method = 'POST', body) {
      if (!url || String(method).toUpperCase() === 'GET') return false;

      try {
        const path = new URL(url, window.location.origin).pathname;
        if (!/\/cart\/add(\.js|\.json)?$/.test(path)) return false;
      } catch (error) {
        return false;
      }

      const data = BbBundle.parseBodyData(body);
      if (data) {
        const lineItems = BbBundle.getLineItemsFromData(data);
        if (lineItems.length > 1) return false;
        if ((Array.isArray(data.items) || Object.keys(data).some((key) => key.startsWith('items['))) && lineItems.length !== 1) {
          return false;
        }
      }

      return true;
    }

    getMissingItems(selectedItems, oldCart, currentCart) {
      if (!oldCart || !currentCart) return selectedItems;

      return selectedItems.reduce((items, selectedItem) => {
        const oldQuantity = this.getCartVariantQuantity(oldCart, selectedItem.id);
        const currentQuantity = this.getCartVariantQuantity(currentCart, selectedItem.id);
        const alreadyAdded = Math.max(currentQuantity - oldQuantity, 0);
        const missingQuantity = selectedItem.quantity - alreadyAdded;

        if (missingQuantity > 0) {
          items.push({ id: selectedItem.id, quantity: missingQuantity });
        }

        return items;
      }, []);
    }

    getCartVariantQuantity(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return 0;

      return cart.items.reduce((quantity, item) => {
        return Number(item.variant_id) === Number(variantId)
          ? quantity + Number(item.quantity || 0)
          : quantity;
      }, 0);
    }

    async fetchCart() {
      const response = await fetch(this.cartRoute, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(response.status);
      return response.json();
    }

    updateCartIcon(data) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (!cartIconBubble || !data.sections || !data.sections['cart-icon-bubble']) return;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = data.sections['cart-icon-bubble'];
      const section = tmpl.content.querySelector('.shopify-section');
      cartIconBubble.innerHTML = section ? section.innerHTML : tmpl.innerHTML;

      const nativeBadges = Array.from(cartIconBubble.querySelectorAll('.header__cart-count'));
      nativeBadges.slice(1).forEach((badge) => badge.remove());
      if (nativeBadges.length > 0) {
        cartIconBubble.querySelectorAll('.cart-count-bubble').forEach((badge) => badge.remove());
      }
    }

    async notifyCartUpdated(addResponse) {
      const cart = await this.fetchCart().catch(() => null);
      const detail = { source: 'bb-bundle', response: addResponse, cart };
      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerSection = cartDrawer
        ? cartDrawer.closest('.shopify-section')?.id?.replace('shopify-section-', '')
        : null;

      document.dispatchEvent(new CustomEvent('bb-bundle:cart:add', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
      window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
      window.dispatchEvent(new CustomEvent('ocu:cart:open'));

      if (
        cartDrawer
        && typeof cartDrawer.renderContents === 'function'
        && cartDrawerSection
        && addResponse.sections
        && addResponse.sections[cartDrawerSection]
      ) {
        await cartDrawer.renderContents(addResponse, true);
      } else if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh(true);
      }

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true, detail }));
    }

    get cartRoute() {
      return window.theme?.routes?.cart
        ? `${window.theme.routes.cart}.js`
        : '/cart.js';
    }

    get cartAddRoute() {
      return window.theme?.routes?.cartAdd || '/cart/add.js';
    }

    get sectionsToRender() {
      const sections = ['cart-icon-bubble'];
      const cartDrawerSection = document
        .querySelector('cart-drawer')
        ?.closest('.shopify-section')
        ?.id
        ?.replace('shopify-section-', '');

      if (cartDrawerSection) sections.push(cartDrawerSection);
      return sections.join(',');
    }

    updateSummary() {
      const selectedVariants = this.getSelectedVariants();
      this.updateButtonLabel(selectedVariants.length);
    }

    getSelectedVariants() {
      return this.rows.reduce((variants, row) => {
        const checkbox = row.querySelector('[data-bb-bundle-checkbox]');
        const variant = this.getRowVariant(row);

        if (checkbox && checkbox.checked && !checkbox.disabled && variant && variant.available) {
          variants.push(variant);
        }

        return variants;
      }, []);
    }

    updateButtonLabel(selectedCount) {
      if (!this.addButton || !this.dynamicButtonLabel) return;
      if (this.addButton.disabled || this.addButton.getAttribute('aria-disabled') === 'true') return;

      if (selectedCount === 0) {
        this.addButton.textContent = this.defaultButtonText;
        return;
      }

      const totalItems = this.getMainQuantity() + selectedCount;
      this.addButton.textContent = `${this.defaultButtonText} (${totalItems} ${this.dataset.itemPlural || 'items'})`;
    }

    get dynamicButtonLabel() {
      // Buy box v2 : si dg-buybox-summary.js pilote le sous-texte du bouton
      // ([data-bb-atc-subtotal]), bb-bundle ne touche PAS au bouton (sinon
      // textContent détruirait la structure 2 lignes .dg-bb__add-main/__add-sub).
      var f = this.form || this.closest('form');
      if (f && f.querySelector('[data-bb-atc-subtotal]')) return false;
      return this.dataset.dynamicButtonLabel !== 'false';
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
          style: 'currency',
          currency: this.data.currency || 'EUR'
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2).replace('.', ',')} ${this.data.currencySymbol || '€'}`;
      }
    }
  }

  customElements.define('bb-bundle', BbBundle);
}());
